<template>
  <vx-card title="Cargos por calificación final" id="card">
    <!-- LOADING -->
    <div
      v-if="uiState === 'loading'"
      class="gw-empty-state"
      style="height:350px;"
    >
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-empty-title">Cargando información…</div>
      <div class="gw-empty-subtitle">Un momento por favor</div>
    </div>

    <!-- READY -->
    <div v-else-if="uiState === 'ready' && hasAnyData" class="vx-row">
      <div
        v-for="block in blocks"
        :key="block.key"
        class="vx-col w-full lg:w-1/2 mb-base"
      >
        <div class="chart-title">{{ block.title }}</div>

        <!-- si un bloque particular no trae data -->
        <div
          v-if="!block.series || block.series.length === 0"
          class="gw-empty-state"
          style="height:320px;"
        >
          <img
            class="gw-empty-img"
            :src="IMG_EMPTY"
            alt="No se encontraron resultados"
          />
          <div class="gw-empty-title">Sin datos para el filtro seleccionado</div>
          <div class="gw-empty-subtitle">Prueba con otro rango de fechas o ciudad.</div>
        </div>

        <VueApexCharts
          v-else
          type="donut"
          height="320"
          :options="block.options"
          :series="block.series"
        />
      </div>
    </div>

    <!-- EMPTY / ERROR (global) -->
    <div
      v-else
      class="gw-empty-state"
      style="height:350px;"
    >
      <img
        class="gw-empty-img"
        :src="emptyStateImage"
        :alt="emptyStateAlt"
      />
      <div class="gw-empty-title">{{ emptyStateTitle }}</div>
      <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
    </div>
  </vx-card>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      requestKey: null,

      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      // Estructura fija por calificación para render
      blocks: [
        { key: 1, title: "Confiables", series: [], options: null },
        { key: 2, title: "Confiables con observaciones", series: [], options: null },
        { key: 0, title: "No confiables", series: [], options: null },
        { key: 7, title: "Finalizados con restricciones", series: [], options: null },
      ],
    };
  },

  components: {
    VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    hasAnyData() {
      return this.blocks.some((b) => Array.isArray(b.series) && b.series.length > 0);
    },

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },
    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },
    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },
    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      const path = `/metrics/occupations-by-overall-final?client_id=${this.userInfo.user.client_id}`;
      let url = `${this.url()}${path}`;

      if (this.startDate != null && this.endDate != null) {
        url += `&start_date=${this.startDate}&end_date=${this.endDate}`;
      }
      if (this.cityId != null) {
        url += `&city_id=${this.cityId}`;
      }

      url += `&top_n=10`;
      return url;
    },

    baseChartOptions(labels) {
      return {
        labels,
        colors: [
          "#5F0091",
          "#ffc300",
          "#c88170",
          "#c5c870",
          "#5eab2c",
          "#ff3200",
          "#0d2cc8",
          "#c20dc8",
          "#bf0093",
          "#616a6b",
          "#0e6251",
          "#d35400",
          "#17202a",
          "#f5cba7",
        ],
        legend: { position: "right" },
        responsive: [
          {
            breakpoint: 480,
            options: { legend: { position: "bottom" } },
          },
        ],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(2) + "%";
          },
        },
      };
    },

    resetBlocks() {
      this.blocks = this.blocks.map((b) => ({
        ...b,
        series: [],
        options: this.baseChartOptions([]),
      }));
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetBlocks();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetBlocks();
    },

    setError() {
      this.uiState = "error";
      this.resetBlocks();
    },

    hydrateBlocks(rows) {
      const byFinal = new Map();
      rows.forEach((r) => {
        const k = Number(r.overall_final);
        if (!byFinal.has(k)) byFinal.set(k, []);
        byFinal.get(k).push(r);
      });

      this.blocks = this.blocks.map((b) => {
        const items = byFinal.get(b.key) || [];

        const labels = items.map((i) => i.name);
        const series = items.map((i) => Number(i.count) || 0);

        const title =
          items.length > 0 && items[0].overall_final_name
            ? items[0].overall_final_name
            : b.title;

        return {
          ...b,
          title,
          series,
          options: this.baseChartOptions(labels),
        };
      });
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      const key = `${this.startDate || ""}|${this.endDate || ""}|${this.cityId || ""}`;
      this.setLoading(key);

      console.log("[candidate-by-occupation] initializeData:", {
        origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        const request = await this.requestGetWithoutContent(this.getUrl(), {});

        // race condition guard
        if (this.requestKey !== key) {
          console.log("[candidate-by-occupation] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn("[candidate-by-occupation] request not ok:", request && request.status);
          this.setError();
          return;
        }

        const data = Array.isArray(request.data) ? request.data : [];

        if (!data.length) {
          this.setEmpty();
          return;
        }

        this.hydrateBlocks(data);

        // si después de hidratar no hay nada, es empty
        if (!this.hasAnyData) {
          this.setEmpty();
          return;
        }

        this.uiState = "ready";
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[candidate-by-occupation] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");
    eventBus.$on("consultData", () => this.initializeData("eventBus:consultData"));
  },
};
</script>

<style scoped>
.chart-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
